import { Controller } from "@hotwired/stimulus"
import { Chart, LineController, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Filler } from "chart.js"

Chart.register(LineController, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Filler)

// The line wears the brand gradient rather than a literal ink: --gradient-from
// and --gradient-to are the theme's two stops, and a brand host overrides them
// at :root (Brand#theme_css), so the chart follows the host like everything
// else. Grid and ticks are alpha over the ground instead of ramp steps, which
// keeps them recessive in both modes — the admin is dark on the house, light
// on a brand's host, fixed server-side.
const token = (name, fallback) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback

const channels = (hex) => hex.replace("#", "").match(/../g).map((c) => parseInt(c, 16))
const rgba = (hex, a) => `rgba(${channels(hex).join(",")},${a})`
const midpoint = (a, b) =>
  "#" + channels(a).map((c, i) => Math.round((c + channels(b)[i]) / 2).toString(16).padStart(2, "0")).join("")

// Renders the cumulative user-growth line chart on the admin Dashboard from a
// [{ label, cumulative }] array supplied by Admin::DashboardStats.
export default class extends Controller {
  static values = { data: Array }

  connect() {
    const dark = document.documentElement.classList.contains("dark")
    const from = token("--gradient-from", "#3B82F6")
    const to = token("--gradient-to", "#C05CF6")
    const surface = token("--surface-base", dark ? "#0F1216" : "#FFFFFF")
    const grid = dark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)"
    const ink = dark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.45)"

    // Both gradients are scriptable: the plot area only exists after the first
    // layout, and it changes again on every resize.
    const stroke = ({ chart }) => {
      if (!chart.chartArea) return to
      const g = chart.ctx.createLinearGradient(chart.chartArea.left, 0, chart.chartArea.right, 0)
      g.addColorStop(0, from)
      g.addColorStop(1, to)
      return g
    }
    // The fill fades the two stops' midpoint down to nothing on the baseline —
    // a second horizontal gradient under the line would only muddy it.
    const fill = ({ chart }) => {
      if (!chart.chartArea) return "transparent"
      const mid = midpoint(from, to)
      const g = chart.ctx.createLinearGradient(0, chart.chartArea.top, 0, chart.chartArea.bottom)
      g.addColorStop(0, rgba(mid, 0.22))
      g.addColorStop(1, rgba(mid, 0))
      return g
    }

    this.chart = new Chart(this.element, {
      type: "line",
      data: {
        labels: this.dataValue.map(d => d.label),
        datasets: [{
          data: this.dataValue.map(d => d.cumulative),
          borderColor: stroke,
          backgroundColor: fill,
          borderWidth: 2,
          // A point is the ground punched through the gradient: the ring picks
          // up the line's colour where it stands.
          pointRadius: 4,
          pointHoverRadius: 6,
          pointHitRadius: 16,
          pointBorderWidth: 2,
          pointBorderColor: stroke,
          pointBackgroundColor: surface,
          fill: true,
          tension: 0.3,
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        // Anywhere on a day reads that day, not just the 8px of its point.
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: { displayColors: false, padding: 10, cornerRadius: 8 },
        },
        scales: {
          x: { grid: { display: false }, border: { color: grid }, ticks: { color: ink, font: { size: 11 } } },
          y: { beginAtZero: true, grid: { color: grid }, border: { display: false },
               ticks: { color: ink, precision: 0, font: { size: 11 } } }
        }
      }
    })
  }

  disconnect() { this.chart?.destroy() }
}
